import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import ConfirmDialog from '@/components/ConfirmDialog';
import { showToast } from '@/components/Toast';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

interface DeleteConversationDialogProps {
  isOpen: boolean;
  conversationTitle?: string;
  onConfirm: () => Promise<any>;
  onCancel: () => void;
}

/**
 * 可复用的删除会话确认对话框
 * 包含 loading 状态和成功/失败提示
 */
export default function DeleteConversationDialog({
  isOpen,
  conversationTitle,
  onConfirm,
  onCancel,
}: DeleteConversationDialogProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    let toastMessage = '';
    try {
      const res = await onConfirm();
      // 支持两种返回格式：直接成功或返回 { code: 200 }
      if (res?.code === 200) {
        toastMessage = t('toast.conversationDeleted');
      }
      // 非 200 的业务错误：api.ts 的 request() 已自动显示错误 toast，无需重复
    } catch (_error) {
      // 网络错误等异常：axios 拦截器已处理，无需重复显示
    } finally {
      setIsDeleting(false);
      setTimeout(() => {
        onCancel();
        if (toastMessage) {
          showToast(toastMessage, 'success');
        }
      }, 0);
    }
  };

  const handleCancel = () => {
    if (isDeleting) return;
    onCancel();
  };

  const message = conversationTitle
    ? t('confirmDialog.deleteConversation', { title: conversationTitle })
    : t('confirmDialog.deleteConversation');

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={t('dialog.deleteConversation', { title: conversationTitle })}
      message={message}
      confirmText={t('common.delete')}
      cancelText={t('common.cancel')}
      type="danger"
      isLoading={isDeleting}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
}
